import { uniq } from 'lodash';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
  ticketActivitiesSet,
  ticketDocumentVersionsSet,
  ticketParticipantsSet,
} from '~/actions/ticket';
import {
  addTicketParticipants,
  getActivities,
  getTicketParticipants,
  uploadNewDocumentVersion,
} from '~/api/tickets';
import { showToast } from '~/components/Shared/EcToast/showToast';
import DescriptionTextAreaForm from '~/components/Workflow/shared/DescriptionTextAreaForm';
import { Button } from '~/eds/src/components/Button/Button';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { CharacterLimits } from '~/enums/CharacterLimits';
import { EntityType } from '~/enums/EntityType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';
import { PartySelectField } from '~/features/turn-tracking/PartySelectField';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import * as toastTypes from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Switch from '~/ui/components/Switch/Switch';
import Text from '~/ui/components/Text/Text';
import * as enums from '~/ui/enums/input';
import { getMentions } from '~/utils/comments';
import { coerceFileType } from '~/utils/files';
import { getUserName, testIsEverestAdmin } from '~/utils/user';
import { getAcceptedFiles } from '~/utils/workflow';

import AddParticipantToTicketPopover from './AddParticipantToTicketPopover';

function UploadVersionAction({
  disabled,
  iconOnly = false,
  // connected
  ticket,
  ticketActivitiesSet,
  ticketParticipantsSet,
  ticketDocumentVersionsSet,
  ticketId,
  documentId,
  //injected
  users,
  onUpload,
}) {
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const mentionInputRef = useRef();
  const intl = useIntl();
  const { navigate, params } = useRouting();
  const { versionNumber } = params;
  const currentVersion = versionNumber || ticket.documentVersions.length;
  const versionNumberExists = !!versionNumber;
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState('');
  const [isCounterparty, setIsCounterparty] = useState(true);
  const [isKeepingUploadedFilename, setIsKeepingUploadedFilename] =
    useState(false);
  const commentExceedLimit = comment.length > enums.CHARACTER_INPUT_LIMIT_LONG;
  const [addParticipantPopoverData, setAddParticipantPopoverData] = useState({
    isVisible: false,
    participant: {},
  });
  const [usersToAddAsParticipants, setUsersToAddAsParticipants] = useState([]);
  const [isPrimaryActionLoading, setIsPrimaryActionLoading] = useState(false);
  const [isSecondaryActionLoading, setIsSecondaryActionLoading] =
    useState(false);
  const [turnTrackingPartyId, setTurnTrackingPartyId] = useState();

  const isChatbotVisible = useSelector(
    slices.chatbot.selectors.selectIsChatbotVisible,
  );

  async function uploadTicketDocVersion(mentions = null, isRedlining = false) {
    const res = await uploadNewDocumentVersion({
      ticketId,
      documentId,
      file,
      text: comment,
      isCounterparty,
      isKeepingUploadedFilename,
      userIds: mentions,
      turnTracking: { partyId: turnTrackingPartyId },
    });
    // push the new version to redux
    ticketDocumentVersionsSet([
      { ...res, fileType: coerceFileType(res.fileType) },
      ...ticket.documentVersions,
    ]);

    dispatch(
      api.util.invalidateTags([
        TAGS.TAG_CURRENT_VERSION,
        TAGS.TURN_TRACKING_TAG,
      ]),
    );
    const activities = await getActivities({
      entityId: ticketId,
      entityType: EntityType.Ticket,
    });
    ticketActivitiesSet(activities);

    let successfulMessage = intl.formatMessage({
      id: 'UploadVersionAction.newVersionUploaded',
      defaultMessage: 'New document version successfully uploaded.',
      description: 'Message when new version is uploaded successfully',
    });

    if (isChatbotVisible) {
      dispatch(slices.chatbot.actions.closeChatbot());
      successfulMessage +=
        ' The Ask AI panel closed as you uploaded the latest document version. Reopen the Ask AI panel to ask questions on the latest document version.';
    }

    showToast(toastTypes.SUCCESS, successfulMessage);

    const newVersionNumber = res.versionNumber;
    if (isRedlining) {
      navigate(
        `/workflow/tickets/${ticketId}/reviewer/${newVersionNumber}?compareTo=${currentVersion}`,
      );
    } else if (versionNumberExists) {
      navigate(`/workflow/tickets/${ticketId}/reviewer/${newVersionNumber}`);
    }
  }
  const fileNameExceedsLimit = file?.name?.length > CharacterLimits.FileName;

  const resetForm = () => {
    // set to default values
    setIsPrimaryActionLoading(false);
    setIsSecondaryActionLoading(false);
    setFile(null);
    setComment('');
    setIsCounterparty(true);
    setIsKeepingUploadedFilename(false);
    setAddParticipantPopoverData({ isVisible: false, participant: {} });
    setUsersToAddAsParticipants([]);
  };

  async function addParticipantsToTicket(allMentions) {
    const usersToAdd = allMentions.filter((id) => !ticket.participants[id]);
    if (usersToAdd && usersToAdd.length > 0) {
      await addTicketParticipants({ ticketId, userIds: usersToAdd });
      showToast(
        toastTypes.SUCCESS,
        `${getUserNameText(usersToAdd)}
    ${
      usersToAdd.length > 1 ? 'have' : 'has'
    } been added to the list of participants.`,
      );
      handleGetParticipants();
    }
  }

  async function onUploadClick(isRedlining = false) {
    try {
      if (onUpload) {
        onUpload?.();
        hideModal();
      }
      const allMentions = uniq(getMentions(comment));
      await addParticipantsToTicket(allMentions);
      await uploadTicketDocVersion(allMentions, isRedlining);
    } catch {
      showToast(
        toastTypes.ERROR,
        intl.formatMessage({
          id: 'UploadVersionAction.newVersionUpload',
          defaultMessage: 'New version upload failed. Please try again.',
          description: 'Error message when new version upload fails',
        }),
      );
    } finally {
      // set to default values
      setIsPrimaryActionLoading(false);
      setIsSecondaryActionLoading(false);
      hideModal();
    }
  }

  const { executor: handleGetParticipants } = useAsync(
    getTicketParticipants,
    { ticketId },
    {
      condition: true,
      successHandler: ({ data }) => ticketParticipantsSet(data),
    },
  );

  function getUserNameText(userIds) {
    const userNames = userIds.map((id) => getUserName(users[id], intl));
    if (userNames.length === 1) return userNames[0];
    else if (userNames.length === 2) return userNames.join(' and ');
    else return `${userNames[0]} and ${userNames.length - 1} other people`;
  }

  function closePopover() {
    setAddParticipantPopoverData({
      isVisible: false,
    });
  }

  function onMentionUser(id) {
    if (
      !ticket.participants[id] &&
      !usersToAddAsParticipants.some((userId) => userId === id)
    ) {
      let user = users[id];
      setAddParticipantPopoverData({
        isVisible: true,
        participant: {
          name: getUserName(user, intl),
          id,
        },
      });
    }
  }

  function removeUserMention(userId) {
    setComment(comment.replace(`{{user:${userId}}}`, ''));
  }

  function onCloseAddParticipantPopOver() {
    removeUserMention(addParticipantPopoverData.participant.id);
    closePopover();
  }

  function onAcceptAddParticipantPopOver({ id }) {
    setUsersToAddAsParticipants([...usersToAddAsParticipants, id]);
    closePopover();
  }

  function onUpdateComment(comment) {
    if (!addParticipantPopoverData.isVisible) {
      setComment(comment);
    }
  }

  const getUploadButtonTooltip = () => {
    if (!file) {
      return intl.formatMessage({
        id: 'UploadVersionAction.attachFileTooltip',
        defaultMessage: 'Attach a file',
        description: 'Tooltip when no file is attached',
      });
    } else if (fileNameExceedsLimit) {
      return intl.formatMessage(
        {
          id: 'UploadVersionAction.fileNameExceedsLimitTooltip',
          defaultMessage: 'File name exceeds {limit} character limit',
          description:
            'Tooltip when file name is too long. Example: "File name exceeds 100 character limit"',
        },
        {
          limit: CharacterLimits.FileName,
        },
      );
    } else if (commentExceedLimit) {
      return intl.formatMessage(
        {
          id: 'UploadVersionAction.notesExceedsLimitTooltip',
          defaultMessage: 'Notes exceeds {limit} character limit',
          description: 'Tooltip when notes exceed character limit',
        },
        { limit: enums.CHARACTER_INPUT_LIMIT_LONG },
      );
    } else {
      return null;
    }
  };

  function getUploadRedlinesButtonTooltip(
    isFileAttachedNotDocx,
    isBaseVersionNotDocx,
  ) {
    let tooltipMessage = getUploadButtonTooltip();
    if (!tooltipMessage) {
      if (isFileAttachedNotDocx || isBaseVersionNotDocx) {
        tooltipMessage = intl.formatMessage({
          id: 'UploadVersionAction.onlyDocxFilesTooltip',
          defaultMessage: 'Only .doc(x) files can be compared',
          description: 'Tooltip when non-docx files cannot be compared',
        });
      }
    }

    return tooltipMessage;
  }

  const isFileAttachedNotDocx = file && file.type !== FileMimeType.Docx;
  const isBaseVersionNotDocx =
    ticket.documentVersions.find(
      (v) => v.versionNumber === Number(currentVersion),
    )?.fileType !== coerceFileType(FileExtensionType.Docx);

  const [modal, showModal, hideModal] = useModal({
    title: intl.formatMessage({
      id: 'UploadVersionAction.uploadNewVersionTitle',
      defaultMessage: 'Upload New Version',
      description: 'upload new version text title',
    }),
    onHide: resetForm,
    primaryAction: {
      disabled:
        !file ||
        commentExceedLimit ||
        addParticipantPopoverData.isVisible ||
        isSecondaryActionLoading ||
        fileNameExceedsLimit,
      text: intl.formatMessage({
        id: 'UploadVersionAction.uploadButtonText',
        defaultMessage: 'Upload',
        description: 'Primary upload button text',
      }),
      tooltip: getUploadButtonTooltip(),
      onClick: () => {
        setIsPrimaryActionLoading(true);
        onUploadClick();
      },
      isLoading: isPrimaryActionLoading,
    },
    secondaryActions: [
      {
        disabled:
          isFileAttachedNotDocx ||
          isBaseVersionNotDocx ||
          !file ||
          commentExceedLimit ||
          isPrimaryActionLoading ||
          fileNameExceedsLimit,
        text: intl.formatMessage({
          id: 'UploadVersionAction.uploadGenerateRedlines',
          defaultMessage: 'Upload & Generate Redlines',
          description: 'Upload and generate redlines text',
        }),
        tooltip: getUploadRedlinesButtonTooltip(
          isFileAttachedNotDocx,
          isBaseVersionNotDocx,
        ),
        variant: 'secondary',
        onClick: () => {
          setIsSecondaryActionLoading(true);
          onUploadClick(true);
        },
        isLoading: isSecondaryActionLoading,
      },
    ],
    children: (
      <>
        <Box mb={4}>
          <Alert enableIcon variant="warning">
            <Text color="gray-700" variant="body2">
              {intl.formatMessage({
                id: 'UploadVersionAction.afterNewVersionUploadAlert',
                defaultMessage:
                  'After a new version of the document is uploaded, editing the intake form will no longer generate new document versions.',
                description: 'Alert message for upload new version action',
              })}
            </Text>
          </Alert>
        </Box>
        {file ? (
          <FlexLayout
            alignItems="center"
            bg="gray-200"
            justifyContent="space-between"
            p={6}
            space={3}
            sx={{ border: 'border', borderRadius: 'm' }}
          >
            <FileLabel file={file} shouldTruncate />
            <Icon
              color="gray-700"
              icon="close"
              size="m"
              title={intl.formatMessage({
                id: 'UploadVersionAction.removeFileTitle',
                defaultMessage: 'Remove file',
                description: 'Title for remove file icon',
              })}
              onClick={() => setFile(null)}
            />
          </FlexLayout>
        ) : (
          <FileInput
            acceptedFiles={getAcceptedFiles(ticket.workflow, [
              FileExtensionType.Docx,
            ])}
            enableDropzone
            enableUpload
            onChange={(files) => setFile(files[0])}
          />
        )}
        <Box mt={3}>
          <DescriptionTextAreaForm
            description={comment}
            id="workflow--ticket-upload-version-add-notes"
            limit={enums.CHARACTER_INPUT_LIMIT_LONG}
            title={intl.formatMessage({
              id: 'UploadVersionAction.notesTitle',
              defaultMessage: 'notes',
              description: 'Title for notes text area',
            })}
            onUpdate={onUpdateComment}
            userMentionsData={users}
            onMentionUser={onMentionUser}
            mentionInputRef={mentionInputRef}
          />
          <AddParticipantToTicketPopover
            {...addParticipantPopoverData}
            onClose={onCloseAddParticipantPopOver}
            onSubmit={onAcceptAddParticipantPopOver}
          />
        </Box>
        <PartySelectField
          workflowId={ticket.workflowId}
          onChange={(value) => setTurnTrackingPartyId(value)}
          errorMessage={{
            message: intl.formatMessage({
              id: 'UploadVersionAction.tryAgainIn',
              defaultMessage: 'Try again in a few minutes.',
              description: 'Error message when turn tracking fails',
            }),
            title: intl.formatMessage({
              id: 'UploadVersionAction.wereExperiencingDifficulty',
              defaultMessage:
                'We’re experiencing difficulty loading Responsible Parties.',
              description:
                'Error message when loading responsible parties fails',
            }),
          }}
        />
        <FlexLayout mt={5} space={3}>
          <Switch
            id="workflow--ticket-upload-version-counterparty-paper"
            size="s"
            value={isCounterparty}
            onChange={(updatedValue) => setIsCounterparty(updatedValue)}
          />
          <Text color="gray-900" variant="body2">
            {intl.formatMessage({
              id: 'UploadVersionAction.markAsCounterpartyDocument',
              defaultMessage: 'Mark as Counterparty document',
              description:
                'Label for the counterparty paper switch in upload new version modal',
            })}
          </Text>
        </FlexLayout>
        {testIsEverestAdmin(currentUser) && (
          <FlexLayout mt={5} space={3}>
            <Switch
              id="workflow--ticket-upload-version-keep-filename"
              size="s"
              value={isKeepingUploadedFilename}
              onChange={(updatedValue) =>
                setIsKeepingUploadedFilename(updatedValue)
              }
            />
            <Text color="gray-900" variant="body2">
              {intl.formatMessage({
                id: 'UploadVersionAction.keepFileNameAfterUpload',
                defaultMessage: 'Keep file name after upload',
                description:
                  'Label for the keep uploaded filename switch in upload new version modal',
              })}
            </Text>
          </FlexLayout>
        )}
        <Box mt={3}>
          <Text color="gray-600" variant="tiny">
            {intl.formatMessage({
              id: 'UploadVersionAction.allTrackedChangesAttribution',
              defaultMessage:
                'All tracked changes included in this document version will be attributed to the counterparty.',
              description: 'Description for upload new version action',
            })}
          </Text>
        </Box>
      </>
    ),
    disableHideOnEscape: true,
    onCancel: () => {
      setComment('');
      setFile(null);
      setAddParticipantPopoverData({ isVisible: false });
      setUsersToAddAsParticipants([]);
    },
  });

  return (
    <>
      {iconOnly ? (
        <IconButton
          disabled={disabled}
          icon="upload"
          size="m"
          tooltip={
            disabled
              ? undefined
              : intl.formatMessage({
                  id: 'UploadVersionAction.uploadNewVersion',
                  defaultMessage: 'Upload new version',
                  description: 'Upload new version text',
                })
          }
          onClick={showModal}
        />
      ) : (
        <Button
          tooltip={
            disabled
              ? undefined
              : intl.formatMessage({
                  id: 'UploadVersionAction.uploadNewVersion',
                  defaultMessage: 'Upload new version',
                  description: 'Upload new version text',
                })
          }
          disabled={disabled}
          iconPosition="left"
          text={intl.formatMessage({
            id: 'UploadVersionAction.uploadNewVersionButtonText',
            defaultMessage: 'New Version',
            description: 'Upload new version text',
          })}
          onClick={showModal}
        />
      )}
      {modal}
    </>
  );
}

const mapStateToProps = ({ ticket }) => {
  const { id: ticketId, document } = ticket;
  return { ticket, ticketId, documentId: document.id };
};

export default withUsers(
  connect(mapStateToProps, {
    ticketActivitiesSet,
    ticketDocumentVersionsSet,
    ticketParticipantsSet,
  })(UploadVersionAction),
);
